
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Tag,
  Spin,
  Button,
  Table,
  Tooltip,
  Progress,
  Tabs,
  Empty,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  listApplications,
  getApplicationDocuments,
} from "../../../../core/data/redux/services/recruitmentApi";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { all_routes } from "../../../router/all_routes";
import RecruitmentHeader from "../../components/RecruitmentHeader";
import { StatTile, ContactItem } from "../../applications/components/CandidateProfileParts";
import Footer from "../../components/Footer";



const STATUS_COLORS: Record<string, string> = {
  applied: "#3b82f6",
  shortlisted: "#8b5cf6",
  screening: "#f59e0b",
  interview: "#06b6d4",
  offer: "#22c55e",
  hired: "#16a34a",
  rejected: "#ef4444",
  withdrawn: "#94a3b8",
};



interface Application {
  application_id: number;
  application_code: string;
  status: string;
  ats_score: string | null;
  applied_at: string;
  updated_at: string;
  source: string;
  priority: string;
  job: {
    job_id: number;
    job_title: string;
    job_code: string;
    work_location: string;
  };
  candidate: {
    candidate_id: number;
    full_name: string;
    email: string;
    phone: string;
    current_company: string | null;
    current_designation: string | null;
    total_experience_years: number | null;
    key_skills: string[];
    profile_pic_serve_url?: string;
  };
}

interface Document {
  id: number;
  document_type: string;
  document_name: string;
  file_name: string;
  status: string;
  uploaded_at: string;
  verification_status: string;
}



const getGradient = (name: string) => {
  const hash = Array.from(name || "C").reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0,
  );
  const hue1 = hash % 360;
  const hue2 = (hue1 + 40) % 360;
  return `linear-gradient(135deg, hsl(${hue1}, 70%, 55%), hsl(${hue2}, 80%, 45%))`;
};

const toTitleCase = (str: string) =>
  str?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "";



interface SectionPanelProps {
  id: string;
  icon: string;
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  accentColor?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const SectionPanel: React.FC<SectionPanelProps> = ({
  id,
  icon,
  title,
  subtitle,
  badge,
  accentColor = "#4f46e5",
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className="section-panel"
      style={{
        background: "#fff",
        border: "1px solid #e8eaf0",
        borderRadius: 14,
        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
        overflow: "hidden",
        transition: "box-shadow 0.2s",
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`section-body-${id}`}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "14px 20px",
          background: open ? "#fafbff" : "#fff",
          border: "none",
          borderBottom: open ? "1px solid #eef0f8" : "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.18s",
        }}
      >
        <div
          style={{
            width: 3,
            height: 32,
            borderRadius: 99,
            background: accentColor,
            flexShrink: 0,
            opacity: open ? 1 : 0.35,
            transition: "opacity 0.2s",
          }}
        />
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: `${accentColor}14`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className={`ti ${icon}`} style={{ fontSize: 18, color: accentColor }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#0f172a",
                letterSpacing: "-0.2px",
              }}
            >
              {title}
            </span>
            {badge}
          </div>
          {subtitle && (
            <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 1 }}>
              {subtitle}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 20,
            background: open ? `${accentColor}12` : "#f1f5f9",
            transition: "background 0.18s",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: open ? accentColor : "#94a3b8",
              transition: "color 0.18s",
            }}
          >
            {open ? "Collapse" : "Expand"}
          </span>
          <i
            className="ti ti-chevron-up"
            style={{
              fontSize: 14,
              color: open ? accentColor : "#94a3b8",
              transform: open ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.25s ease, color 0.18s",
              display: "block",
            }}
          />
        </div>
      </button>

      <div id={`section-body-${id}`} style={{ display: open ? "block" : "none" }}>
        {children}
      </div>

      <style>{`.section-panel:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.08) !important; }`}</style>
    </div>
  );
};



const CandidateView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const activeCompany = useSelector(
    (state: any) => state.recruitment.activeCompany,
  );
  const companyId = activeCompany?.company_id;
  const companyType = activeCompany?.company_type;

  const [applications, setApplications] = useState<Application[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState<Application["candidate"] | null>(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!id || !companyId) return;
    const candidateId = parseInt(id);

    const fetchData = async () => {
      setLoading(true);
      try {
        const appData = await listApplications(companyId, companyType, {
          candidate_id: candidateId,
          limit: 100,
          page: 1,
        } as any);

        const apps: Application[] = appData?.applications ?? [];
        setApplications(apps);

        if (apps.length > 0) {
          setCandidate(apps[0].candidate);

          try {
            const docs = await getApplicationDocuments(
              companyId,
              companyType,
              apps[0].application_id,
            );
            setDocuments(Array.isArray(docs) ? docs : docs?.documents ?? []);
          } catch {
            setDocuments([]);
          }
        }
      } catch (err) {
        console.error("[CandidateView] fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, companyId, companyType]);


  const appColumns: ColumnsType<Application> = [
    {
      title: "Sr.",
      key: "srNo",
      width: 55,
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {index + 1}
        </span>
      ),
    },
    {
      title: "Job",
      key: "job",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{r.job?.job_title}</div>
          <div style={{ fontSize: 11.5, color: "#6b7280" }}>
            <i className="ti ti-map-pin me-1" />
            {r.job?.work_location}
          </div>
        </div>
      ),
    },
    {
      title: "Application ID",
      dataIndex: "application_code",
      key: "code",
      render: (code) => (
        <span className="badge badge-md badge-info-transparent">{code}</span>
      ),
    },
    {
      title: "ATS Score",
      dataIndex: "ats_score",
      key: "ats",
      align: "center",
      render: (score) => {
        const num = score ? parseFloat(score) : null;
        if (!num) return <span className="text-muted fs-12">N/A</span>;
        return (
          <Progress
            type="dashboard"
            percent={Math.round(num)}
            size={40}
            strokeColor={
              num >= 80 ? "#22c55e" : num >= 60 ? "#f59e0b" : "#ef4444"
            }
            format={(percent) => (
              <span style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>
                {percent}%
              </span>
            )}
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const color = STATUS_COLORS[status] || "#94a3b8";
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              borderRadius: 999,
              padding: "3px 10px",
              fontSize: 11.5,
              fontWeight: 600,
              backgroundColor: `${color}1A`,
              color,
              border: `1px solid ${color}40`,
              textTransform: "capitalize",
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: color,
                flexShrink: 0,
              }}
            />
            {status?.replace(/_/g, " ")}
          </span>
        );
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (val) => {
        if (!val) return <span className="text-muted">—</span>;
        const map: Record<string, string> = {
          urgent: "#ef4444",
          high: "#f97316",
          normal: "#3b82f6",
          low: "#94a3b8",
        };
        const color = map[val] || "#94a3b8";
        return (
          <span
            style={{
              display: "inline-block",
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              background: `${color}1A`,
              color,
              border: `1px solid ${color}40`,
            }}
          >
            {val}
          </span>
        );
      },
    },
    {
      title: "Applied",
      dataIndex: "applied_at",
      key: "applied_at",
      render: (date) => (
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {humanReadableDate(date)}
        </span>
      ),
    },
  ];


  const docColumns: ColumnsType<Document> = [
    {
      title: "Document",
      key: "name",
      render: (_, r) => (
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 38, height: 38, background: "#fee2e2" }}
          >
            <i className="ti ti-file-type-pdf text-danger" style={{ fontSize: 18 }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.document_name}</div>
            <div style={{ fontSize: 11.5, color: "#6b7280" }}>{r.document_type}</div>
          </div>
        </div>
      ),
    },
    {
      title: "File",
      dataIndex: "file_name",
      key: "file",
      render: (name) => (
        <span style={{ fontSize: 12, color: "#374151" }}>{name}</span>
      ),
    },
    {
      title: "Verification",
      dataIndex: "verification_status",
      key: "v_status",
      width: 130,
      render: (vs) => {
        const color =
          vs === "verified" ? "#16a34a" : vs === "rejected" ? "#ef4444" : "#f59e0b";
        return (
          <span
            style={{
              display: "inline-block",
              borderRadius: 999,
              padding: "3px 10px",
              fontSize: 11.5,
              fontWeight: 600,
              textTransform: "capitalize",
              background: `${color}1A`,
              color,
              border: `1px solid ${color}40`,
            }}
          >
            {vs || "pending"}
          </span>
        );
      },
    },
    {
      title: "Uploaded",
      dataIndex: "uploaded_at",
      key: "uploaded_at",
      width: 130,
      render: (date) => (
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {humanReadableDate(date)}
        </span>
      ),
    },
  ];


  if (loading) {
    return (
      <div className="page-wrapper">
        <div
          className="content d-flex justify-content-center align-items-center"
          style={{ minHeight: "70vh" }}
        >
          <div className="text-center">
            <Spin size="large" />
            <p className="text-muted mt-3" style={{ fontSize: 14 }}>
              Loading candidate profile…
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link
            to={all_routes.recruitmentCandidatesList}
            className="btn btn-sm btn-white mb-4 shadow-sm"
          >
            <i className="ti ti-arrow-left me-1" /> Back to Candidates
          </Link>
          <Empty
            description="Candidate not found or no applications for this company."
            style={{ background: "#fff", padding: "40px", borderRadius: 14 }}
          />
        </div>
      </div>
    );
  }


  const skills = candidate.key_skills || [];
  const mostRecentApp = applications[0];

  return (
    <div className="page-wrapper">
      <div
        className="content"
        style={{ padding: "20px 24px", maxWidth: 1400, margin: "0 auto" }}
      >
        <RecruitmentHeader
          title="Candidate Details"
          breadcrumb="Candidate Details"
        />

        <div className="mb-4">
          <Link
            to={all_routes.recruitmentCandidatesList}
            className="btn btn-sm btn-white shadow-sm"
            style={{
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              padding: "6px 14px",
            }}
          >
            <i className="ti ti-arrow-left me-2" />
            Back to Candidates
          </Link>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>


          <SectionPanel
            id="profile"
            icon="ti-user-circle"
            title="Candidate Profile"
            subtitle={`${candidate.full_name} · ${candidate.current_designation || "—"}`}
            accentColor="#4f46e5"
            defaultOpen
          >

            <div style={{ height: 3, background: getGradient(candidate.full_name) }} />

            <div className="p-4 pb-3">
              <div className="d-flex align-items-center gap-0">


                <div
                  className="d-flex align-items-center gap-3 flex-shrink-0"
                  style={{
                    minWidth: 280,
                    paddingRight: 20,
                    borderRight: "1px solid #f0f0f8",
                  }}
                >
                  <div className="position-relative flex-shrink-0">
                    {candidate.profile_pic_serve_url && !imgError ? (
                      <img
                        src={candidate.profile_pic_serve_url}
                        alt={candidate.full_name}
                        style={{
                          width: 64,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: 14,
                          border: "2.5px solid #f0f0f0",
                        }}
                        onError={() => setImgError(true)}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                        style={{
                          width: 64,
                          height: 64,
                          fontSize: 20,
                          borderRadius: 14,
                          background: getGradient(candidate.full_name),
                        }}
                      >
                        {(candidate.full_name || "?")
                          .split(" ")
                          .map((n) => n[0])
                          .slice(0, 2)
                          .join("")
                          .toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <h4
                      className="fw-bold mb-0 text-truncate"
                      style={{ fontSize: 18, color: "#0f172a" }}
                    >
                      {candidate.full_name}
                    </h4>
                    <div
                      className="text-truncate"
                      style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}
                    >
                      {candidate.current_designation}
                      {candidate.current_company && (
                        <span style={{ color: "#94a3b8" }}>
                          {" "}· {candidate.current_company}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          borderRadius: 999,
                          padding: "2px 8px",
                          fontSize: 10,
                          fontWeight: 600,
                          background: "#f0f4ff",
                          color: "#4f46e5",
                          border: "1px solid #dde3ff",
                        }}
                      >
                        <i
                          className="ti ti-folders"
                          style={{ fontSize: 10, marginRight: 4 }}
                        />
                        {applications.length} Application
                        {applications.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                </div>


                <div
                  className="d-flex align-items-center flex-grow-1 overflow-auto"
                  style={{ padding: "0 20px", gap: 0 }}
                >
                  <StatTile
                    icon="ti-folders"
                    label="Applications"
                    value={applications.length}
                    accent="#4f46e5"
                  />
                  <StatTile
                    icon="ti-clock-star"
                    label="Experience"
                    value={
                      candidate.total_experience_years != null
                        ? `${candidate.total_experience_years}y`
                        : "N/A"
                    }
                    accent="#0ea5e9"
                  />
                  <StatTile
                    icon="ti-award"
                    label="Skills"
                    value={skills.length || "—"}
                    accent="#10b981"
                  />
                  {mostRecentApp && (
                    <StatTile
                      icon="ti-calendar"
                      label="Last Applied"
                      value={humanReadableDate(mostRecentApp.applied_at)}
                      accent="#f59e0b"
                    />
                  )}
                </div>
              </div>


              <div
                style={{ height: 1, background: "#f1f5f9", margin: "16px 0 14px" }}
              />


              <div className="d-flex align-items-center gap-4 flex-wrap">
                <ContactItem
                  icon="ti-mail"
                  label="Email"
                  value={candidate.email}
                  accent="#4f46e5"
                  href={`mailto:${candidate.email}`}
                />
                <ContactItem
                  icon="ti-phone"
                  label="Phone"
                  value={candidate.phone}
                  accent="#0ea5e9"
                  href={candidate.phone ? `tel:${candidate.phone}` : undefined}
                />

                {skills.length > 0 && (
                  <>
                    <div
                      className="flex-shrink-0"
                      style={{ width: 1, height: 28, background: "#e2e8f0" }}
                    />
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#b0bec5",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Skills
                      </span>
                      {skills.slice(0, 5).map((s) => (
                        <span
                          key={s}
                          className="rounded-pill"
                          style={{
                            fontSize: 11,
                            padding: "3px 10px",
                            background: "#f0f4ff",
                            color: "#4f46e5",
                            border: "1px solid #dde3ff",
                            fontWeight: 600,
                          }}
                        >
                          {s}
                        </span>
                      ))}
                      {skills.length > 5 && (
                        <span
                          className="rounded-pill"
                          style={{
                            fontSize: 11,
                            padding: "3px 10px",
                            background: "#f1f5f9",
                            color: "#94a3b8",
                            border: "1px solid #e2e8f0",
                            fontWeight: 600,
                          }}
                        >
                          +{skills.length - 5} more
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </SectionPanel>


          <SectionPanel
            id="details"
            icon="ti-layout-list"
            title="Details & Documents"
            subtitle="Application history · Documents · Skills"
            accentColor="#10b981"
            badge={
              applications.length > 0 ? (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    borderRadius: 999,
                    padding: "2px 8px",
                    fontSize: 10,
                    fontWeight: 600,
                    background: "#dcfce7",
                    color: "#16a34a",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <i className="ti ti-clipboard-list" style={{ fontSize: 10 }} />
                  {applications.length} app
                  {applications.length !== 1 ? "s" : ""}
                </span>
              ) : undefined
            }
            defaultOpen
          >
            <Tabs
              defaultActiveKey="history"
              className="candidate-detail-tabs"
              tabBarStyle={{
                padding: "0 20px",
                marginBottom: 0,
                background: "#fafbfc",
                borderBottom: "1px solid #e9ecef",
              }}
              items={[

                {
                  key: "history",
                  label: (
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      <i
                        className="ti ti-clipboard-list me-2"
                        style={{ fontSize: 14 }}
                      />
                      Application History
                      {applications.length > 0 && (
                        <span
                          className="badge bg-primary rounded-pill ms-1"
                          style={{ fontSize: 10 }}
                        >
                          {applications.length}
                        </span>
                      )}
                    </span>
                  ),
                  children: (
                    <div className="p-4" style={{ background: "#fafbfc" }}>
                      <div
                        className="bg-white"
                        style={{
                          borderRadius: 12,
                          border: "1px solid #e8eaf0",
                          overflow: "hidden",
                        }}
                      >
                        <Table
                          columns={appColumns}
                          dataSource={applications}
                          rowKey="application_id"
                          size="middle"
                          pagination={applications.length > 10 ? { pageSize: 10, size: "small" } : false}
                          scroll={{ x: 900 }}
                          locale={{
                            emptyText: (
                              <Empty description="No applications found" />
                            ),
                          }}
                        />
                      </div>
                    </div>
                  ),
                },


                {
                  key: "documents",
                  label: (
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      <i
                        className="ti ti-files me-2"
                        style={{ fontSize: 14 }}
                      />
                      Documents
                      {documents.length > 0 && (
                        <span
                          className="badge bg-success rounded-pill ms-1"
                          style={{ fontSize: 10 }}
                        >
                          {documents.length}
                        </span>
                      )}
                    </span>
                  ),
                  children: (
                    <div className="p-4">
                      {documents.length > 0 ? (
                        <div className="d-flex flex-column gap-3">
                          {documents.map((doc) => {
                            const verColor =
                              doc.verification_status === "verified"
                                ? "#16a34a"
                                : doc.verification_status === "rejected"
                                  ? "#ef4444"
                                  : "#f59e0b";
                            return (
                              <div
                                key={doc.id}
                                className="d-flex align-items-center gap-3 p-3 rounded-3 border"
                                style={{ background: "#fff", borderColor: "#e9ecef" }}
                              >
                                <div
                                  className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                  style={{ width: 46, height: 46, background: "#fee2e2" }}
                                >
                                  <i
                                    className="ti ti-file-text text-danger"
                                    style={{ fontSize: 22 }}
                                  />
                                </div>
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                  <div
                                    className="fw-semibold text-dark text-truncate mb-1"
                                    style={{ fontSize: 14 }}
                                  >
                                    {doc.document_name}
                                  </div>
                                  <div
                                    className="d-flex gap-2 text-muted"
                                    style={{ fontSize: 12 }}
                                  >
                                    <span className="text-capitalize">
                                      {doc.document_type}
                                    </span>
                                    <span>·</span>
                                    <span>{humanReadableDate(doc.uploaded_at)}</span>
                                    <span>·</span>
                                    <span className="text-truncate">{doc.file_name}</span>
                                  </div>
                                </div>
                                <span
                                  style={{
                                    display: "inline-block",
                                    borderRadius: 999,
                                    padding: "3px 12px",
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    textTransform: "capitalize",
                                    flexShrink: 0,
                                    background: `${verColor}1A`,
                                    color: verColor,
                                    border: `1px solid ${verColor}40`,
                                  }}
                                >
                                  {doc.verification_status || "pending"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div
                          className="text-center py-5"
                          style={{ minHeight: 280 }}
                        >
                          <div
                            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                            style={{ width: 80, height: 80, background: "#f1f3f9" }}
                          >
                            <i
                              className="ti ti-files text-muted opacity-50"
                              style={{ fontSize: 40 }}
                            />
                          </div>
                          <h6 className="fw-bold text-dark">
                            No documents available
                          </h6>
                          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                            This candidate has not uploaded any documents yet.
                          </p>
                        </div>
                      )}
                    </div>
                  ),
                },


                {
                  key: "skills",
                  label: (
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      <i className="ti ti-star me-2" style={{ fontSize: 14 }} />
                      Skills
                      {skills.length > 0 && (
                        <span
                          className="badge bg-warning rounded-pill ms-1"
                          style={{ fontSize: 10 }}
                        >
                          {skills.length}
                        </span>
                      )}
                    </span>
                  ),
                  children: (
                    <div className="p-4">
                      {skills.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {skills.map((s, i) => (
                            <span
                              key={i}
                              style={{
                                fontSize: 13,
                                padding: "6px 14px",
                                borderRadius: 8,
                                background: "#f0f4ff",
                                color: "#4f46e5",
                                border: "1px solid #dde3ff",
                                fontWeight: 600,
                              }}
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-5 text-muted">
                          <i
                            className="ti ti-star-off d-block mb-3 opacity-25"
                            style={{ fontSize: 48 }}
                          />
                          <h6 className="fw-bold text-dark">No skills listed</h6>
                          <p style={{ fontSize: 13 }}>
                            This candidate hasn't added any skills to their profile.
                          </p>
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </SectionPanel>

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CandidateView;